import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Search,
  Filter,
  Mail,
  MessageSquare,
  Megaphone,
  Image as ImageIcon,
  TrendingUp,
  Loader2,
  RefreshCcw,
  Eye,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const AllCampaignsPage = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  // State
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (channelFilter !== 'all') params.append('channel', channelFilter);
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const data = await apiCall(`/api/admin/marketing/all-campaigns${queryString}`);
      setCampaigns(data);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      toast({
        title: 'Error',
        description: 'Failed to load campaigns',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [apiCall, channelFilter, statusFilter, toast]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'sms':
        return <MessageSquare className="h-4 w-4" />;
      case 'whatsapp':
        return <MessageSquare className="h-4 w-4 text-green-600" />;
      case 'marketplace_ads':
        return <Megaphone className="h-4 w-4" />;
      default:
        return <ImageIcon className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'default',
      scheduled: 'secondary',
      completed: 'outline',
      draft: 'outline',
      pending_review: 'warning',
      needs_revision: 'warning',
      rejected: 'destructive',
      paused: 'secondary',
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  const filteredCampaigns = campaigns.filter(c => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        c.campaign_name.toLowerCase().includes(search) ||
        c.vendor_name.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Calculate totals
  const totals = filteredCampaigns.reduce(
    (acc, c) => ({
      budget: acc.budget + (c.budget || 0),
      spent: acc.spent + (c.spent || 0),
      impressions: acc.impressions + (c.impressions || 0),
      clicks: acc.clicks + (c.clicks || 0),
      conversions: acc.conversions + (c.conversions || 0),
    }),
    { budget: 0, spent: 0, impressions: 0, clicks: 0, conversions: 0 }
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">All Campaigns (SSOT)</h1>
            <p className="text-muted-foreground">
              Global view of all marketing campaigns across all channels and vendors
            </p>
          </div>
          <Button onClick={fetchCampaigns} variant="outline" disabled={loading}>
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Total Budget</p>
              <p className="text-xl font-bold">₹{totals.budget.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Total Spent</p>
              <p className="text-xl font-bold">₹{totals.spent.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Impressions</p>
              <p className="text-xl font-bold">{totals.impressions.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Clicks</p>
              <p className="text-xl font-bold">{totals.clicks.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Conversions</p>
              <p className="text-xl font-bold">{totals.conversions.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search campaigns or vendors..."
              className="w-64"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Channels</SelectItem>
                <SelectItem value="marketplace_ads">Marketplace Ads</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Campaigns Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Campaign Overview
            </CardTitle>
            <CardDescription>
              {filteredCampaigns.length} campaigns total
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                No campaigns found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Budget</TableHead>
                      <TableHead className="text-right">Spent</TableHead>
                      <TableHead className="text-right">Impr.</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead className="text-right">Conv.</TableHead>
                      <TableHead>Dates</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCampaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {getChannelIcon(campaign.channel)}
                            <span className="max-w-[200px] truncate">
                              {campaign.campaign_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {campaign.vendor_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{campaign.channel}</Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                        <TableCell className="text-right">
                          ₹{campaign.budget?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          ₹{campaign.spent?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          {campaign.impressions?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          {campaign.clicks?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          {campaign.conversions?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {campaign.start_date && (
                            <div>{new Date(campaign.start_date).toLocaleDateString()}</div>
                          )}
                          {campaign.end_date && (
                            <div>to {new Date(campaign.end_date).toLocaleDateString()}</div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AllCampaignsPage;
